import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { BookBoxesJobBookApiBody } from '@/models/pacBookBoxesJobBooking';
import type { PacJobBookApiBody } from '@/models/pacJobBooking';

import { AxiosPromise, AxiosRequestConfig } from 'axios';

import { api } from '@/api';

export async function getJobTicketsSearchByJob(
  params: openapi.JobGetTicketsByJobShortQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobGetTicketsByJobShortContentOpenapi> {
  return await api.get<openapi.JobGetTicketsByJobShortContentOpenapi>(
    '/job/tickets/search_by_job/',
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobMessageVisibility(
  params: openapi.JobMessageVisibleUpdateQueryOpenapi,
  data: openapi.JobMessageVisibleUpdateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobMessageVisibleUpdateContentOpenapi> {
  return await api.post<openapi.JobMessageVisibleUpdateContentOpenapi>(
    '/job/message/visibility/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function getJobAirroad(
  params: openapi.JobGetAirroadQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobGetAirroadContentOpenapi> {
  return await api.get<openapi.JobGetAirroadContentOpenapi>('/job/airroad/', {
    ...requestConfig,
    params,
  });
}

export async function postJobAttachment(
  params: openapi.PortalJobAttachQueryOpenapi,
  data: {
    rh_id: number;
    rh_date: string;
    files: File[];
  }, // TODO as openapi.PortalJobAttachBodyOpenapi
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PortalJobAttachContentOpenapi> {
  const bodyFormData = new FormData();
  bodyFormData.append('rh_id', data.rh_id.toString());
  bodyFormData.append('rh_date', data.rh_date);
  data.files.forEach((file) => {
    bodyFormData.append('file', file);
  });
  return await api.post<openapi.PortalJobAttachContentOpenapi>(
    '/job/attachment/',
    bodyFormData,
    {
      ...requestConfig,
      params,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}

export async function postJobStopImage(
  params: openapi.DownloadStopImagesQueryOpenapi,
  data: {
    job_id: number;
    job_date: string;
    image: File;
    stop_number: number;
    ref: string;
  }, // TODO as openapi.DownloadStopImagesBodyOpenapi
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.DownloadStopImagesContentOpenapi> {
  const bodyFormData = new FormData();
  bodyFormData.append('job_id', data.job_id.toString());
  bodyFormData.append('job_date', data.job_date);
  bodyFormData.append('image', data.image);
  bodyFormData.append('stop_number', data.stop_number.toString());
  bodyFormData.append('ref', data.ref);
  return await api.post<openapi.DownloadStopImagesContentOpenapi>(
    '/job/stop/image/',
    bodyFormData,
    {
      ...requestConfig,
      params,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}

export async function deleteJobStopImage(
  params: openapi.DeleteJobStopImage_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.DeleteJobStopImage_204> {
  return await api.delete<openapi.DeleteJobStopImage_204>('/job/stop/image/', {
    ...requestConfig,
    params,
  });
}

export async function deleteJobAttachmentById(
  params: openapi.PortalJobDeleteAttachQueryOpenapi,
  path: openapi.PortalJobDeleteAttachPathOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PortalJobDeleteAttachContentOpenapi> {
  return await api.delete<openapi.PortalJobDeleteAttachContentOpenapi>(
    `/job/attachment/${path.id}/`,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function getJobHistory(
  params: openapi.JobHistoryQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobHistoryContentOpenapi> {
  return await api.get<openapi.JobHistoryContentOpenapi>('/job/history/', {
    ...requestConfig,
    params,
  });
}

export async function postJobAllocation(
  params: openapi.JobAllocationCreateQueryOpenapi,
  data: openapi.JobAllocationCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobAllocationCreateContentOpenapi> {
  return await api.post<openapi.JobAllocationCreateContentOpenapi>(
    '/job/allocation/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobMessage(
  params: openapi.DriverSendMessageCreateQueryOpenapi,
  data: openapi.DriverSendMessageCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.DriverSendMessageCreateContentOpenapi> {
  return await api.post<openapi.DriverSendMessageCreateContentOpenapi>(
    '/job/message/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function putJobCanSchedule(
  params: openapi.JobCanscheduleCreateQueryOpenapi,
  data: openapi.JobCanscheduleCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobCanscheduleCreateContentOpenapi> {
  return await api.put<openapi.JobCanscheduleCreateContentOpenapi>(
    '/job/can_schedule/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobFutile(
  params: openapi.JobFutileCreateQueryOpenapi,
  data: openapi.JobFutileCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobFutileCreateContentOpenapi> {
  return await api.post<openapi.JobFutileCreateContentOpenapi>(
    '/job/futile/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobCancel(
  params: openapi.JobCancelQueryOpenapi,
  data: openapi.JobCancelBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobCancelContentOpenapi> {
  return await api.post<openapi.JobCancelContentOpenapi>('/job/cancel/', data, {
    ...requestConfig,
    params,
  });
}

export async function postJobSetOnHold(
  params: openapi.JobSetOnHoldQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobSetOnHoldContentOpenapi> {
  return await api.post<openapi.JobSetOnHoldContentOpenapi>(
    '/job/set_on_hold/',
    null,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobUnsetOnHold(
  params: openapi.JobUnsetOnHoldQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobUnsetOnHoldContentOpenapi> {
  return await api.post<openapi.JobUnsetOnHoldContentOpenapi>(
    '/job/unset_on_hold/',
    null,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function getJobPriceSc(
  params: openapi.GetJobPriceSc_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetJobPriceSc_200> {
  return await api.get<openapi.GetJobPriceSc_200>('/job/price_sc/', {
    ...requestConfig,
    params,
  });
}

export async function getJobResync(
  params: openapi.JobResyncQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobResyncContentOpenapi> {
  return await api.get<openapi.JobResyncContentOpenapi>('/job/resync/', {
    ...requestConfig,
    params,
  });
}

export async function getJobPrintoutFile(
  params: openapi.JobPrintoutListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobPrintoutListContentOpenapi> {
  return await api.get<openapi.JobPrintoutListContentOpenapi>(
    `/job/printout/`,
    {
      ...requestConfig,
      params,
      responseType: 'blob',
    },
  );
}

export async function getJobDraft(
  params: openapi.JobDraftListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobDraftListContentOpenapi> {
  return await api.get<openapi.JobDraftListContentOpenapi>(`/job/draft/`, {
    ...requestConfig,
    params,
  });
}

export async function postJobDraft(
  data: openapi.PostJobDraft_Body,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostJobDraft_201> {
  return await api.post<openapi.PostJobDraft_201>(
    '/job/draft/',
    data,
    requestConfig,
  );
}

export async function getJobDetailed(
  params: openapi.DetailedJobNewQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.DetailedJobNewContentOpenapi> {
  return await api.get<openapi.DetailedJobNewContentOpenapi>('/job/detailed/', {
    ...requestConfig,
    params,
  });
}

export async function getJobDetailedByRhId(
  params: openapi.GetJobDetailedByRhId_Query,
  path: openapi.GetJobDetailedByRhId_Path,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetJobDetailedByRhId_200> {
  return await api.get<openapi.GetJobDetailedByRhId_200>(
    `/job/detailed/${path.rh_id}/`,
    {
      ...requestConfig,
      params,
    },
  );
}

export async function getJobSearch(
  params: openapi.GetJobSearch_1_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetJobSearch_1_200> {
  return await api.get<openapi.GetJobSearch_1_200>('/job/search/', {
    ...requestConfig,
    params,
  });
}

export async function getJobSearchCount(
  params: openapi.GetJobSearch_2_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetJobSearch_2_200> {
  return await api.get<openapi.GetJobSearch_2_200>('/job/search/', {
    ...requestConfig,
    params,
  });
}

export async function getDashboardSearchPdf(
  params: openapi.GetDashboardSearchPdf_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetDashboardSearchPdf_200> {
  return await api.get<openapi.GetDashboardSearchPdf_200>(
    '/dashboard/search/pdf/',
    {
      ...requestConfig,
      params,
      responseType: 'blob',
    },
  );
}

export async function getJobSearchByPostcode(
  params: openapi.JobSearchJobByPostcodeQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobSearchJobByPostcodeContentOpenapi> {
  return await api.get<openapi.JobSearchJobByPostcodeContentOpenapi>(
    '/job/search_by_postcode/',
    {
      ...requestConfig,
      params,
    },
  );
}

export async function getJobLocation(
  params: openapi.JobLocationsQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.JobLocationsContentOpenapi> {
  return await api.get<openapi.JobLocationsContentOpenapi>('/job/location/', {
    ...requestConfig,
    params,
  });
}

export async function getJobStopImage(
  params: openapi.GetStopImagesQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetStopImagesContentOpenapi> {
  return await api.get<openapi.GetStopImagesContentOpenapi>(
    '/job/stop/image/',
    {
      ...requestConfig,
      params,
    },
  );
}

export async function postJobBookApi(
  params: openapi.PostJobBookApi_Query,
  data: openapi.PostJobBookApi_Body,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostJobBookApi_201> {
  return await api.post<openapi.PostJobBookApi_201>('/job/book_api/', data, {
    ...requestConfig,
    params: {
      dry_run: false,
      ...params,
    },
  });
}

// TODO: replace response type with the real openapi type once the endpoint is defined.
export async function postPacJobBookApi(
  data: PacJobBookApiBody,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostJobBookApi_201> {
  return await api.post<openapi.PostJobBookApi_201>(
    '/pac/book_api/',
    data,
    requestConfig,
  );
}

// TODO: replace response type with the real openapi type once the endpoint is defined
export async function postBookBoxesJobBookApi(
  data: BookBoxesJobBookApiBody,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostJobBookApi_201> {
  return await api.post<openapi.PostJobBookApi_201>(
    '/pac/book_boxes_api/',
    data,
    requestConfig,
  );
}

export async function postJobBookXls(
  params: openapi.PostJobBookXls_Query,
  file: File,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostJobBookXls_201> {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return await api.post<openapi.PostJobBookXls_201>(
    '/job/book_xls/',
    bodyFormData,
    {
      ...requestConfig,
      params: {
        dry_run: false,
        ...params,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}
